import { Component, Input } from '@angular/core';
import { DateTime } from 'luxon';
import { DataServiceService } from '../../services/data-service.service';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { Trailers, Customers, Orders, LoadTypes } from '../../interfaces';

@Component({
  selector: 'app-edit-order-dialog',
  templateUrl: './edit-order-dialog.component.html',
  styleUrl: './edit-order-dialog.component.scss'
})
export class EditOrderDialogComponent {

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  @Input() allOrders: Orders[] = [];
  @Input() customers: Customers[] = [];
  @Input() trailers: Trailers[] = [];
  @Input() loadTypesArray: LoadTypes[] = [];
  @Input() loadType: any[] = [];

  defaultOrderState = {
    order_id: '',
    trailer_id: '',
    external_trailer_number: '',
    supplier_id: '',
    receiver_id: '',
    supplier_address_id: '',
    receiver_address_id: '',
    supplier_selection: '',
    receiver_selection: '',
    date_received: new Date().toLocaleString('en-US'),
    pickup_driver_id: '',
    pickup_date: '',
    delivery_driver_id: '',
    delivery_date: '',
    load_type: '',
    load_status: 'Unassigned',
    pickup_number: '',
    notes: '',
    time_loaded: '',
    time_unloaded: '',
    time_delivered: ''
  };

  editVisible: boolean = false;
  isExternalTrailer: boolean = false;
  editingOrder: any = { ...this.defaultOrderState };
  currentReceiverOptions: any[] = [];
  currentSupplierOptions: any[] = [];
  visible: boolean = false;
  selectedOrderId: number | undefined;
  showPickupTimePicker: boolean = false;
  showDeliveryTimePicker: boolean = false;


  // Filtered customer arrays for dropdowns
  get supplierCustomers() {
    return this.customers.filter(customer => 
      customer.customer_type === 'Supplier' || customer.customer_type === 'Both'
    );
  }

  get receiverCustomers() {
    return this.customers.filter(customer => 
      customer.customer_type === 'Receiver' || customer.customer_type === 'Both'
    );
  }

  // Combined customer+address options for suppliers
  get supplierOptions() {
    const options: any[] = [];
    this.supplierCustomers.forEach(customer => {
      if (customer.addresses && customer.addresses.length > 0) {
        // Multiple addresses: create one option per address with compact format
        customer.addresses.forEach((address: any) => {
          options.push({
            name: `${customer.name} (${address.city}, ${address.state})`,
            customer_id: customer.customer_id,
            address_id: address.address_id,
            value: `${customer.customer_id}_${address.address_id}`
          });
        });
      } else {
        // Single/no address: just the customer name
        options.push({
          name: customer.name,
          customer_id: customer.customer_id,
          address_id: null,
          value: customer.customer_id.toString()
        });
      }
    });
    return options;
  }

  // Combined customer+address options for receivers
  get receiverOptions() {
    const options: any[] = [];
    this.receiverCustomers.forEach(customer => {
      if (customer.addresses && customer.addresses.length > 0) {
        // Multiple addresses: create one option per address with compact format
        customer.addresses.forEach((address: any) => {
          options.push({
            name: `${customer.name} (${address.city}, ${address.state})`,
            customer_id: customer.customer_id,
            address_id: address.address_id,
            value: `${customer.customer_id}_${address.address_id}`
          });
        });
      } else {
        // Single/no address: just the customer name
        options.push({
          name: customer.name,
          customer_id: customer.customer_id,
          address_id: null,
          value: customer.customer_id.toString()
        });
      }
    });
    return options;
  }

  onTrailerTypeChange() {
    if (this.isExternalTrailer) {
      this.editingOrder.trailer_id = null;
      console.log("trailer_id is null", this.editingOrder.trailer_id);
      this.editingOrder.external_trailer_number = this.editingOrder.external_trailer_number;
    } else {
      
      this.editingOrder.external_trailer_number = '';
    }
  }

  getReceiversForSupplier(supplier_id: number): any[] {
    if (!supplier_id) {
      // No supplier selected - return all receiver options
      return this.receiverOptions;
    }

    const supplier = this.customers.find(c => c.customer_id === supplier_id);
    if (supplier && supplier.receivers && supplier.receivers.length > 0) {
      console.log('Supplier', supplier.receivers);
      const receiverOptions: any[] = [];

      for (const receiver of supplier.receivers) {
        // Find the full customer data for this receiver
        const receiverCustomer = this.customers.find(c => c.customer_id === receiver.customer_id);

        if (receiverCustomer) {
          if (receiverCustomer.addresses && receiverCustomer.addresses.length > 0) {
            // Multiple addresses: create one option per address with compact format
            receiverCustomer.addresses.forEach((address: any) => {
              receiverOptions.push({
                name: `${receiverCustomer.name} (${address.city}, ${address.state})`,
                customer_id: receiverCustomer.customer_id,
                address_id: address.address_id,
                value: `${receiverCustomer.customer_id}_${address.address_id}`
              });
            });
          } else {
            // Single/no address: just the customer name
            receiverOptions.push({
              name: receiverCustomer.name,
              customer_id: receiverCustomer.customer_id,
              address_id: null,
              value: receiverCustomer.customer_id.toString()
            });
          }
        }
      }

      console.log('Receiver Options', receiverOptions);
      receiverOptions.sort((a, b) => a.name.localeCompare(b.name));
      return receiverOptions;
    }
    // If supplier has no specific receivers, return all receiver options
    return this.receiverOptions;
  }

  // Handle edit supplier selection
  onEditSupplierChange(selectedValue: string) {
    if (selectedValue.includes('_')) {
      const parts = selectedValue.split('_');
      this.editingOrder.supplier_id = parseInt(parts[0]);
      this.editingOrder.supplier_address_id = parseInt(parts[1]);
    } else {
      this.editingOrder.supplier_id = parseInt(selectedValue);
      this.editingOrder.supplier_address_id = null;
    }
  }

  // Handle edit receiver selection
  onEditReceiverChange(selectedValue: string) {
    if (selectedValue.includes('_')) {
      const parts = selectedValue.split('_');
      this.editingOrder.receiver_id = parseInt(parts[0]);
      this.editingOrder.receiver_address_id = parseInt(parts[1]);
    } else {
      this.editingOrder.receiver_id = parseInt(selectedValue);
      this.editingOrder.receiver_address_id = null;
    }
  }

  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    
    // Check if the time is midnight in UTC (meaning no specific time was selected)
    if (dt.hour === 0 && dt.minute === 0 && dt.second === 0) {
      const centralTime = dt.setZone('America/Chicago');
      return centralTime.toFormat('MM/dd/yyyy');
    }
    
    // Time was specified, convert to Central and show time
    const centralTime = dt.setZone('America/Chicago');
    return centralTime.toFormat('MM/dd/yyyy h:mm a');
  }


  clearInput() {
    this.editingOrder = { ...this.defaultOrderState };
    this.isExternalTrailer = false;
    this.visible = false;
    this.editVisible = false;
    this.showPickupTimePicker = false;
    this.showDeliveryTimePicker = false;
    this.currentReceiverOptions = [];  // Reset receiver options
    this.currentSupplierOptions = [];  // Reset supplier options
  }


  editOrder(orderId: number) {
    this.selectedOrderId = orderId;
    const orderToEdit = this.allOrders.find(order => order.order_id === orderId);
    console.log("orderToEdit", orderToEdit);
    if (orderToEdit) {
      // Make a copy of the order
      this.editingOrder = { ...orderToEdit };
      
      // Convert UTC dates to Central Time Date objects for the calendar
      // Also check if time was specified (not midnight UTC) and set checkboxes accordingly
      if (this.editingOrder.pickup_date) {
        const hasPickupTime = this.hasSpecificTime(this.editingOrder.pickup_date);
        this.editingOrder.pickup_date = this.convertUTCToCentral(this.editingOrder.pickup_date);
        this.showPickupTimePicker = hasPickupTime;
      }
      if (this.editingOrder.delivery_date) {
        const hasDeliveryTime = this.hasSpecificTime(this.editingOrder.delivery_date);
        this.editingOrder.delivery_date = this.convertUTCToCentral(this.editingOrder.delivery_date);
        this.showDeliveryTimePicker = hasDeliveryTime;
      }
      
      // Convert trailer_id to string if it exists (to match dropdown values)
      if (this.editingOrder.trailer_id) {
        this.editingOrder.trailer_id = this.editingOrder.trailer_id.toString();
      }
      
      // Set up combined supplier selection value
      if (orderToEdit.supplier_address_id) {
        this.editingOrder.supplier_selection = `${orderToEdit.supplier_id}_${orderToEdit.supplier_address_id}`;
      } else {
        this.editingOrder.supplier_selection = orderToEdit.supplier_id.toString();
      }
      
      // Set up combined receiver selection value
      if (orderToEdit.receiver_address_id) {
        this.editingOrder.receiver_selection = `${orderToEdit.receiver_id}_${orderToEdit.receiver_address_id}`;
      } else {
        this.editingOrder.receiver_selection = orderToEdit.receiver_id.toString();
      }
      
      // Set checkbox state based on whether there's an external trailer number
      this.isExternalTrailer = !!orderToEdit.external_trailer_number;
      this.editVisible = true;
    }
  }

  saveEdit() {
    const index = this.allOrders.findIndex((o: { order_id: number | undefined; }) => o.order_id === this.selectedOrderId);
    if (index !== -1) {
      const orderToUpdate = {
        ...this.editingOrder,
        trailer_id: Number(this.editingOrder.trailer_id) || null,
        external_trailer_number: this.isExternalTrailer ? this.editingOrder.external_trailer_number : '',
        supplier_address_id: this.editingOrder.supplier_address_id ? Number(this.editingOrder.supplier_address_id) : null,
        receiver_address_id: this.editingOrder.receiver_address_id ? Number(this.editingOrder.receiver_address_id) : null,
        pickup_date: this.editingOrder.pickup_date ? this.convertCentralToUTC(this.editingOrder.pickup_date) : null,
        delivery_date: this.editingOrder.delivery_date ? this.convertCentralToUTC(this.editingOrder.delivery_date) : null,
        time_loaded: this.editingOrder.time_loaded ? this.convertCentralToUTC(this.editingOrder.time_loaded) : null,
        time_unloaded: this.editingOrder.time_unloaded ? this.convertCentralToUTC(this.editingOrder.time_unloaded) : null,
        time_delivered: this.editingOrder.time_delivered ? this.convertCentralToUTC(this.editingOrder.time_delivered) : null
      };

      this.dataServiceService.updateOrder(this.editingOrder.order_id, orderToUpdate).subscribe({
        next: (response) => {
          this.allOrders[index] = { ...orderToUpdate };
          this.editingOrder = { ...this.defaultOrderState };
          this.editVisible = false;
        }
      });
    }
  }

  deleteOrder(event: any, order: any) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: 'Confirm Order Removal',
      message: 'Are you sure you want to delete this order?',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        const orderId = order.order_id;
        this.dataServiceService.deleteOrder(orderId).subscribe({
          next: (response) => {
            this.allOrders = this.allOrders.filter((o: { order_id: any; }) => o.order_id !== orderId);
            this.editVisible = false;
            this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'Order deleted', life: 3000 });
          }
        });
      },
      reject: () => {
        this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'Order was not deleted', life: 3000 });
      }
    });
  }

  // Check if a date has a specific time (not midnight in Central Time)
  private hasSpecificTime(date: Date | string): boolean {
    const utcDate = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralTime = utcDate.setZone('America/Chicago');
    // If it's midnight in Central Time, treat as "no specific time"
    return !(centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0);
  }

  // Convert UTC date from database to Central Time Date object for calendar display
  private convertUTCToCentral(date: Date | string): Date {
    const utcDate = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralDate = utcDate.setZone('America/Chicago');
    return centralDate.toJSDate();
  }

  // Convert local date to Central Time, then to UTC for database storage
  private convertCentralToUTC(date: Date | string): string {
    const centralDate = DateTime.fromJSDate(new Date(date), { zone: 'America/Chicago' });
    return centralDate.setZone('utc').toISO() as string;
  }
}
